let schema = {};

export const dateTransformer = (x) => x && new Date(x);

export function defineComputedProperty(proto, key, descriptor) {
  Object.defineProperty(proto, key, {
    enumerable: false,
    configurable: false,
    get: descriptor.get,
    set: descriptor.set,
  });
}

// Resolve the local ID storage key behind a relationship.
export function relationshipBackingIdAttr(rel, relationship) {
  return rel + (relationship.repeat ? 'Ids' : 'Id');
}

// Pull the entity ID out of either an object value or an already-normalized ID.
function relationshipValueId(relationship, value) {
  if (value && typeof value === 'object') {
    return value[relationship.entity.idAttr];
  }
  return value;
}

// Convert relationship assignments into the normalized ID representation.
function relationshipValueIds(relationship, value) {
  if (relationship.repeat) {
    if (value == null) return [];
    const values = Array.isArray(value) ? value : [value];
    return values
      .map(v => relationshipValueId(relationship, v))
      .filter(v => v != null);
  }

  return relationshipValueId(relationship, value);
}

// Return relationship IDs as an array so inverse updates can diff old and new state.
function idArrayFromBacking(value, relationship) {
  if (relationship.repeat) {
    return Array.isArray(value) ? value.filter(v => v != null) : [];
  }
  return value == null ? [] : [value];
}

// Compare IDs defensively because object keys may round-trip as strings.
function sameId(a, b) {
  return String(a) === String(b);
}

// Add a child ID to a repeat backing list without creating duplicates.
function addRelationshipId(obj, rel, relationship, id) {
  const backing = relationshipBackingIdAttr(rel, relationship);
  if (relationship.repeat) {
    if (!Array.isArray(obj[backing])) {
      obj[backing] = [];
    }
    if (!obj[backing].some(existing => sameId(existing, id))) {
      obj[backing].push(id);
    }
  }
  else {
    obj[backing] = id;
  }
}

// Remove a child ID from a relationship backing field.
function removeRelationshipId(obj, rel, relationship, id) {
  const backing = relationshipBackingIdAttr(rel, relationship);
  if (relationship.repeat) {
    if (!Array.isArray(obj[backing])) return;
    obj[backing] = obj[backing].filter(existing => !sameId(existing, id));
  }
  else if (sameId(obj[backing], id)) {
    obj[backing] = undefined;
  }
}

// Keep declared inverse relationships in sync when a relationship changes.
function syncInverseRelationship(sourceObj, relationship, oldIds, newIds, store) {
  const inverseRel = relationship.inverse || inferInverseRelationship(relationship);
  if (!inverseRel || !relationship.owner) return;

  const sourceId = sourceObj?.[relationship.owner.idAttr];
  if (sourceId == null) return;

  const inverseRelationship = relationship.entity.relationships?.[inverseRel];
  const targetBucket = store[relationship.entity.key];
  if (!inverseRelationship || !targetBucket) return;

  oldIds.forEach(oldId => {
    if (newIds.some(newId => sameId(newId, oldId))) return;
    const oldObj = targetBucket[oldId];
    if (oldObj) removeRelationshipId(oldObj, inverseRel, inverseRelationship, sourceId);
  });

  newIds.forEach(newId => {
    if (oldIds.some(oldId => sameId(oldId, newId))) return;
    const newObj = targetBucket[newId];
    if (newObj) addRelationshipId(newObj, inverseRel, inverseRelationship, sourceId);
  });
}

// Find the single relationship on the target entity that points back here.
function inferInverseRelationship(relationship) {
  if (relationship.inverse !== undefined) return relationship.inverse;
  const owner = relationship.owner;
  const targetRelationships = relationship.entity.relationships || {};
  const matches = Object.keys(targetRelationships).filter(rel => {
    return targetRelationships[rel].entity === owner;
  });

  relationship.inverse = matches.length === 1 ? matches[0] : null;
  return relationship.inverse;
}

// Set a relationship through its public property while preserving inverse links.
export function setRelationshipValue(obj, rel, relationship, value, store) {
  const backing = relationshipBackingIdAttr(rel, relationship);
  const oldIds = idArrayFromBacking(obj[backing], relationship);
  const normalizedValue = relationshipValueIds(relationship, value);

  obj[backing] = normalizedValue;

  const newIds = idArrayFromBacking(obj[backing], relationship);
  syncInverseRelationship(obj, relationship, oldIds, newIds, store);
}

function assignIdsToDeclaredParent(state, rel, relationship, child) {
  const parentEntity = relationship.parent;
  const parentIdAttr = relationship.idAttr || `${parentEntity.key}Id`;
  const parentBucket = state[parentEntity.key];
  if (!parentBucket) return;

  const children = relationship.repeat ? child : [child];
  const childIdKey = relationship.entity.idAttr;
  const parentRelationship = parentEntity.relationships?.[rel];

  for (let i = 0; i < children.length; i++) {
    const childObj = children[i];
    const parentId = childObj?.[parentIdAttr];
    const childId = childObj?.[childIdKey];
    if (parentId == null || childId == null) continue;

    const parentObj = parentBucket[parentId];
    if (!parentObj) continue;

    if (parentRelationship) {
      addRelationshipId(parentObj, rel, parentRelationship, childId);
    }
    else {
      const parentRelKey = rel + (relationship.repeat ? 'Ids' : 'Id');
      if (relationship.repeat) {
        if (!Array.isArray(parentObj[parentRelKey])) {
          parentObj[parentRelKey] = [];
        }
        if (!parentObj[parentRelKey].some(existing => sameId(existing, childId))) {
          parentObj[parentRelKey].push(childId);
        }
      }
      else {
        parentObj[parentRelKey] = childId;
      }
    }
  }
}

function assignRelationshipIds(targ, state, rel, relationship, child) {
  // Most relationships still attach IDs to the object currently being normalized.
  // When a relationship declares a different `parent`, route those child IDs there
  // instead of writing them onto the traversal source object.
  if (relationship.parent) {
    assignIdsToDeclaredParent(state, rel, relationship, child);
    return;
  }

  setRelationshipValue(targ, rel, relationship, child, state);
}

export function defineRelationshipProperty(proto, rel, relationship, store) {
  Object.defineProperty(proto, rel, {
    enumerable: false,
    configurable: false,
    get: function() {
      const suffix = relationship.repeat ? 'Ids' : 'Id';
      const ids = this[rel + suffix];
      // console.debug(`get ${rel} on ${this.id}`, relationship);

      if (ids == null)
        return relationship.repeat ? [] : null;

      const bucket = store[relationship.entity.key];
      if (!bucket)
        return relationship.repeat ? [] : null;

      if (relationship.repeat) {
        const out = new Array(ids.length);
        for (let i = 0; i < ids.length; i++) {
          out[i] = bucket[ids[i]] || null;
        }
        return out;
      }

      return bucket[ids] || null;
    },
    set: function(value) {
      setRelationshipValue(this, rel, relationship, value, store);
    }
  });
}

export class Entity {
  constructor(store, key) {
    this.store = store;
    this.key = key;
    this.relationships = {}
    this.transforms = {}
    this.proto = {};
    
    schema[key] = this;
  }
  
  define(relationships, idAttr='id') {
    this.idAttr = idAttr;
    
    for (let rel of Object.keys(relationships)) {
      let target = relationships[rel];

      // Support derived prototype properties declared inline in schema.define(),
      // e.g. imageUrl: { get() { ... } }.
      if (target &&
          typeof target === 'object' &&
          !Array.isArray(target) &&
          !target.hasOwnProperty('entity') &&
          (typeof target.get === 'function' || typeof target.set === 'function')) {
        defineComputedProperty(this.proto, rel, target);
        continue;
      }
      
      // Are we handling a list?
      let entity, isArray = false;
      if (target.hasOwnProperty('entity')) {
        // Options
        entity = target.entity
        target = { ...target }
        delete target.entity
      }
      else {
        entity = target;
      }
            
      if (Array.isArray(entity)) {
        entity = entity[0];
        isArray = true;
      }

      // what will this special key do?
      if (! (entity instanceof Entity)) {
        // console.debug(`Add transform ${this.key}-${rel}`, entity);
        this.transforms[rel] = entity
      }
      else {
        const relationship = this.relationships[rel] = { owner: this, repeat: isArray, entity, ...target }
        defineRelationshipProperty(this.proto, rel, relationship, this.store);
      }
    }
  }
}

export function normalize(data, state, entity, parentKey=undefined, parentId=undefined) {
  if (Array.isArray(entity)) {
    entity = entity[0];
    //console.log("%cnormalize:", "font-weight:bold", data.length, entity.key);
    for (let i=0; i<data.length; i++) {
      normalize(data[i], state, entity, parentKey, parentId);
    }
    return;
  }
  
  // ensure the entity bucket exists
  if (!state[entity.key])
    state[entity.key] = Object.create(null);
  
  const id = data[entity.idAttr];
  
  // Pull relationship keys off so they don't interfere with getters and setters.
  let rels = {};
  let relsPresent = {};
  for (let rel of Object.keys(entity.relationships)) {
    if (Object.hasOwn(data, rel)) {
      const relationship = entity.relationships[rel];
      if (data[rel] === undefined || (relationship.repeat && data[rel] === null)) {
        delete data[rel];
        continue;
      }
      rels[rel] = data[rel];
      relsPresent[rel] = true;
      delete data[rel];
    }
  }
  
  // store attributes
  let targ = state[entity.key][id];
  const oldRelationshipIds = {};
  for (let rel of Object.keys(entity.relationships)) {
    const relationship = entity.relationships[rel];
    const backing = relationshipBackingIdAttr(rel, relationship);
    oldRelationshipIds[rel] = targ ? idArrayFromBacking(targ[backing], relationship) : [];
  }
  const incomingAttrs = new Set(Object.keys(data));

  if (!targ) {
    // fast path
    targ = state[entity.key][id] = Object.create(entity.proto);
    Object.assign(state[entity.key][id], data);
  }
  else {
    if (Object.getPrototypeOf(targ) !== entity.proto) {
      Object.setPrototypeOf(targ, entity.proto);
    }

    // upsert accumulate path
    let keys = Object.keys(data);
    for (let i=0; i<keys.length; i++) {
      let k = keys[i];
      if (data[k] === undefined) {
        continue;
      }
      targ[k] = data[k];
    }
  }

  for (let rel of Object.keys(entity.relationships)) {
    if (Object.hasOwn(targ, rel)) {
      delete targ[rel];
    }
  }
  
  // Handle relationships
  if (parentKey) {
    targ[parentKey+'Id'] = parentId;
    incomingAttrs.add(parentKey+'Id');
  }

  for (let rel of Object.keys(entity.relationships)) {
    const relationship = entity.relationships[rel];
    const backing = relationshipBackingIdAttr(rel, relationship);
    if (!incomingAttrs.has(backing)) continue;

    const newIds = idArrayFromBacking(targ[backing], relationship);
    syncInverseRelationship(targ, relationship, oldRelationshipIds[rel], newIds, state);
  }

  for (let rel of Object.keys(entity.relationships)) {
    if (!relsPresent[rel]) continue;

    let child        = rels[rel];
    let relationship = entity.relationships[rel];

    if (child == null) {
      setRelationshipValue(targ, rel, relationship, relationship.repeat ? [] : null, state);
      continue;
    }

    // ingest into store
    // Pass the immediate traversal parent as before; separately, relationship
    // metadata can redirect where the child IDs are attached in state.
    normalize(child, state, 
      relationship.repeat ? [relationship.entity] : relationship.entity,
      entity.key, data[entity.idAttr]
    );
    
    // Replace the nested child payload with IDs on the correct owning object.
    assignRelationshipIds(targ, state, rel, relationship, child);
    
    // Do not hold stale nubs
    delete targ[rel];
  }
  
  // Apply transforms.
  for (let k of Object.keys(entity.transforms)) {
    let transform = entity.transforms[k];
    targ[k] = transform(targ[k]);
  }
}
